/**
 * Little wooden "clack" for each bead dropped, made with WebAudio on the fly (no audio
 * file to vendor). Best effort: if the browser blocks audio, the game just stays quiet.
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Call from a tap so iOS lets later sounds play. */
export function unlockAudio(): void {
  audio();
}

/** One bead landing. `kind` changes the voice a little: a store drop rings lower. */
export function playClack(kind: 'pit' | 'store' | 'capture' = 'pit'): void {
  const ac = audio();
  if (!ac || ac.state !== 'running') return;
  try {
    const t = ac.currentTime;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    const base = kind === 'store' ? 520 : kind === 'capture' ? 660 : 1250;
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(base * (0.9 + Math.random() * 0.25), t);
    osc.frequency.exponentialRampToValueAtTime(base * 0.55, t + 0.06);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(kind === 'pit' ? 0.22 : 0.3, t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + (kind === 'pit' ? 0.07 : 0.16));
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(t);
    osc.stop(t + 0.2);
  } catch {
    // never load-bearing
  }
}

/** A small rising three-note chime for "go again" and wins. */
export function playChirp(): void {
  const ac = audio();
  if (!ac || ac.state !== 'running') return;
  try {
    [660, 830, 990].forEach((f, i) => {
      const t = ac.currentTime + i * 0.08;
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.16, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      osc.connect(gain);
      gain.connect(ac.destination);
      osc.start(t);
      osc.stop(t + 0.25);
    });
  } catch {
    // never load-bearing
  }
}

export function buzz(ms: number): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // no haptics here
  }
}
